import * as React from 'react';
import { useDynamicModule, unspaceString } from '@grandvisionhq/utils';
import { useIntl } from '@grandvisionhq/state';
import { Select, PhoneNumber } from '@grandvisionhq/forms';
import { Alert } from '@grandvisionhq/common';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}
function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var AdyenContext = React.createContext({
    clientKey: '',
    environment: 'test',
    AdyenModule: {
        result: undefined,
        loading: true,
    },
});
var useAdyen = function () { return React.useContext(AdyenContext); };
var AdyenProvider = function (_a) {
    var children = _a.children, settings = _a.settings;
    // The `@adyen/adyen-web` package uses a hard reference to the global `window` variable. This makes it
    // impossible to import the package in a normal manner. Hence the choice to use dynamic importing.
    var _b = __read(useDynamicModule(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(typeof window !== 'undefined')) return [3 /*break*/, 2];
                    return [4 /*yield*/, import('@adyen/adyen-web').then(function (module) { return module.default; })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: return [2 /*return*/, undefined];
            }
        });
    }); }, '@adyen/adyen-web', []), 2), result = _b[0], loading = _b[1];
    var memoizedSettings = React.useMemo(function () { return (__assign(__assign({}, settings), { AdyenModule: { result: result !== null && result !== void 0 ? result : undefined, loading: loading } })); }, [settings, loading, result]);
    return React.createElement(AdyenContext.Provider, { value: memoizedSettings }, children);
};

var AdyenPaymentType;
(function (AdyenPaymentType) {
    AdyenPaymentType["Card"] = "card";
    AdyenPaymentType["Bancontact"] = "bcmc";
    AdyenPaymentType["Sepa"] = "sepadirectdebit";
})(AdyenPaymentType || (AdyenPaymentType = {}));

function useCheckout(onChange) {
    var locale = useIntl().locale;
    // Import the adyen web module
    var _a = useAdyen(), AdyenModule = _a.AdyenModule, clientKey = _a.clientKey, environment = _a.environment;
    var options = React.useMemo(function () { return ({
        clientKey: clientKey,
        environment: environment,
        locale: "" + locale,
        onChange: onChange,
    }); }, [clientKey, environment, locale, onChange]);
    // The checkout object is local to the current page, it needs to be memoized
    // so to handle the dynamic module loading correctly
    var checkout = React.useMemo(function () {
        var AdyenCheckout = AdyenModule === null || AdyenModule === void 0 ? void 0 : AdyenModule.result;
        return AdyenCheckout && AdyenCheckout(options);
    }, [AdyenModule, options]);
    return checkout;
}

var PaymentMethodFormContainer = function (_a) {
    var children = _a.children, type = _a.type, additionalOptions = _a.additionalOptions, checkout = _a.checkout;
    var ref = React.useRef(null);
    // In order to properly unmount any mounted elements, the promise of the mounted element is used to
    // properly unmount it again whenever the cleanup cycle is triggered.
    React.useEffect(function () {
        if (!checkout)
            return;
        var mountPayment = function () { return __awaiter(void 0, void 0, void 0, function () {
            var c, paymentMethod;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, checkout];
                    case 1:
                        c = _b.sent();
                        paymentMethod = c === null || c === void 0 ? void 0 : c.create(type, additionalOptions);
                        return [2 /*return*/, paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.mount((_a = ref.current) !== null && _a !== void 0 ? _a : '')];
                }
            });
        }); };
        var promise = mountPayment();
        return function () {
            promise.then(function (component) { return component === null || component === void 0 ? void 0 : component.unmount(); });
        };
    }, [type, additionalOptions, checkout]);
    if (!checkout)
        return null;
    return (React.createElement("div", { ref: ref, className: "adyen-payment-method-form" }, children));
};

var PaymentMethodForm = function (props) { return (React.createElement(PaymentMethodFormContainer, __assign({}, props))); };

var toPaymentMethodFormState$1 = function (state, cardBrand) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    return ({
        cardBrand: cardBrand,
        isValid: state.isValid,
        data: {
            browserInfo: {
                acceptHeader: (_b = (_a = state === null || state === void 0 ? void 0 : state.data) === null || _a === void 0 ? void 0 : _a.browserInfo) === null || _b === void 0 ? void 0 : _b.acceptHeader,
                userAgent: (_d = (_c = state === null || state === void 0 ? void 0 : state.data) === null || _c === void 0 ? void 0 : _c.browserInfo) === null || _d === void 0 ? void 0 : _d.userAgent,
            },
            holderName: (_g = (_f = (_e = state === null || state === void 0 ? void 0 : state.data) === null || _e === void 0 ? void 0 : _e.paymentMethod) === null || _f === void 0 ? void 0 : _f.holderName) !== null && _g !== void 0 ? _g : '',
            encryptedCardNumber: (_k = (_j = (_h = state === null || state === void 0 ? void 0 : state.data) === null || _h === void 0 ? void 0 : _h.paymentMethod) === null || _j === void 0 ? void 0 : _j.encryptedCardNumber) !== null && _k !== void 0 ? _k : '',
            encryptedExpiryMonth: (_o = (_m = (_l = state === null || state === void 0 ? void 0 : state.data) === null || _l === void 0 ? void 0 : _l.paymentMethod) === null || _m === void 0 ? void 0 : _m.encryptedExpiryMonth) !== null && _o !== void 0 ? _o : '',
            encryptedExpiryYear: (_r = (_q = (_p = state === null || state === void 0 ? void 0 : state.data) === null || _p === void 0 ? void 0 : _p.paymentMethod) === null || _q === void 0 ? void 0 : _q.encryptedExpiryYear) !== null && _r !== void 0 ? _r : '',
            encryptedSecurityCode: (_u = (_t = (_s = state === null || state === void 0 ? void 0 : state.data) === null || _s === void 0 ? void 0 : _s.paymentMethod) === null || _t === void 0 ? void 0 : _t.encryptedSecurityCode) !== null && _u !== void 0 ? _u : '',
        },
    });
};
var CreditCardContainer = function (_a) {
    var onChange = _a.onChange, additionalOptions = _a.additionalOptions, _b = _a.type, type = _b === void 0 ? AdyenPaymentType.Card : _b;
    var _c = __read(React.useState(), 2), state = _c[0], setState = _c[1];
    var _d = __read(React.useState(), 2), brand = _d[0], setBrand = _d[1];
    var checkout = useCheckout(setState);
    var additional = React.useMemo(function () { return (__assign(__assign({ hasHolderName: true, holderNameRequired: true, positionHolderNameOnTop: true }, additionalOptions), { onBrand: function (obj) {
            var _a;
            (_a = additionalOptions === null || additionalOptions === void 0 ? void 0 : additionalOptions.onBrand) === null || _a === void 0 ? void 0 : _a.call(additionalOptions, obj);
            setBrand(obj.brand);
        } })); }, [additionalOptions, setBrand]);
    React.useEffect(function () {
        if (state) {
            onChange(toPaymentMethodFormState$1(state, brand));
        }
    }, [brand, state, onChange]);
    return React.createElement(PaymentMethodForm, { type: type, checkout: checkout, additionalOptions: additional });
};

var CreditCard = function (props) { return React.createElement(CreditCardContainer, __assign({}, props)); };

var IdealContainer = function (_a) {
    var items = _a.items, onChange = _a.onChange;
    var getLabel = useIntl().getLabel;
    var labels = React.useMemo(function () { return ({
        title: getLabel('checkout.payment.method.ideal.select.title'),
    }); }, [getLabel]);
    return (React.createElement(Select, { id: "idealBankSelect", name: "idealBankSelect", options: items, onChange: onChange, label: labels.title, placeholder: labels.title }));
};

var Ideal = function (props) { return React.createElement(IdealContainer, __assign({}, props)); };

var Mbway$1 = function (_a) {
    var initialValue = _a.initialValue, labels = _a.labels, onChange = _a.onChange, validations = _a.validations;
    return (React.createElement("div", { className: "mbway-payment checkout-payment-wrapper" },
        React.createElement(Alert, { className: "mbway-payment__alert", title: labels.hintTitle, message: labels.hintMessage }),
        React.createElement(PhoneNumber, { initialState: { value: initialValue, isTouched: false }, required: true, onChange: onChange, validations: validations, label: labels.phoneNumberLabel, placeholder: labels.phoneNumberPlaceholder, errorMessage: labels.phoneNumberError })));
};

var isValidPortugueseMobilePhoneNumber = function (value) {
    return /^(\+351|00351|351)([9]{1})(6|3|2|1{1})([0-9]{7})$/.test(unspaceString(value));
};

var MbwayContainer = function (_a) {
    var initialValue = _a.initialValue, onChange = _a.onChange;
    var getLabel = useIntl().getLabel;
    var handleOnChange = React.useCallback(function (state) {
        onChange({ telephoneNumber: unspaceString(state.value) }, isValidPortugueseMobilePhoneNumber(state.value));
    }, [onChange]);
    var labels = React.useMemo(function () {
        return {
            hintTitle: getLabel('checkout.payment.method.mbWay.hint.title'),
            hintMessage: getLabel('checkout.payment.method.mbWay.hint.message'),
            phoneNumberLabel: getLabel('checkout.contact.phoneNumber'),
            phoneNumberPlaceholder: getLabel('checkout.payment.method.mbWay.phoneNumber.placeholder'),
            phoneNumberError: getLabel('checkout.contact.phoneNumber.errorMessage'),
        };
    }, [getLabel]);
    return (React.createElement(Mbway$1, { initialValue: initialValue, labels: labels, onChange: handleOnChange, validations: [isValidPortugueseMobilePhoneNumber] }));
};

var Mbway = function (props) { return React.createElement(MbwayContainer, __assign({}, props)); };

var Oney$1 = function (_a) {
    var options = _a.options, labels = _a.labels, onChange = _a.onChange;
    return (React.createElement(Select, { id: "oneyInstallments", name: "oneyInstallments", options: options, onChange: onChange, label: labels.title, placeholder: labels.placeholder, required: true }));
};

var OneyContainer = function (_a) {
    var onChange = _a.onChange, options = _a.options;
    var getLabel = useIntl().getLabel;
    var labels = React.useMemo(function () { return ({
        title: getLabel('checkout.payment.method.oney.select.title'),
        placeholder: getLabel('checkout.payment.method.oney.select.placeholder'),
    }); }, [getLabel]);
    return React.createElement(Oney$1, { labels: labels, onChange: onChange, options: options });
};

var Oney = function (props) { return React.createElement(OneyContainer, __assign({}, props)); };

// This component will create any Adyen web component that can be created from it's "action" prop.
//
// NOTE:
// currently only tested with the "redirect" action
var ActionContainer = function (_a) {
    var action = _a.action;
    var ref = React.useRef(null);
    var checkout = useCheckout();
    // Mounts the action component if we receive an action from adyen
    React.useEffect(function () {
        if (!checkout || !action)
            return;
        var mountAction = function () { return __awaiter(void 0, void 0, void 0, function () {
            var c, actionComponent;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, checkout];
                    case 1:
                        c = _b.sent();
                        actionComponent = c === null || c === void 0 ? void 0 : c.createFromAction(action);
                        return [2 /*return*/, actionComponent === null || actionComponent === void 0 ? void 0 : actionComponent.mount((_a = ref.current) !== null && _a !== void 0 ? _a : '')];
                }
            });
        }); };
        var promise = mountAction();
        return function () {
            promise.then(function (component) { return component === null || component === void 0 ? void 0 : component.unmount(); });
        };
    }, [checkout, action]);
    if (!action) {
        return null;
    }
    return React.createElement("div", { ref: ref, className: "adyen-action-component" });
};

var toPaymentMethodFormState = function (state) {
    var _a, _b;
    return ({
        isValid: state.isValid,
        data: {
            'sepa.ownerName': (_a = state.data.paymentMethod.ownerName) !== null && _a !== void 0 ? _a : '',
            'sepa.ibanNumber': (_b = state.data.paymentMethod.iban) !== null && _b !== void 0 ? _b : '',
        },
    });
};
var SepaContainer = function (_a) {
    var onChange = _a.onChange;
    var setSepaState = React.useCallback(function (state) { return onChange(toPaymentMethodFormState(state)); }, [
        onChange,
    ]);
    var checkout = useCheckout(setSepaState);
    return React.createElement(PaymentMethodForm, { type: AdyenPaymentType.Sepa, checkout: checkout });
};

var Sepa = function (props) { return React.createElement(SepaContainer, __assign({}, props)); };

export { ActionContainer, AdyenPaymentType, AdyenProvider, CreditCard, Ideal, Mbway, Oney, PaymentMethodForm, Sepa, useAdyen };
//# sourceMappingURL=index.es.js.map
